import { useState } from "react";
import { api, type FeedbackEntry, type NewFeedback } from "../lib/api";
import { remember, remembered } from "../lib/remember";
import { VERDICT_LABELS } from "./VerdictChips";

/**
 * The per-run feedback form.
 *
 * INTENTION AND EXPECTATION ARE PREFILLED FROM THE PROMPT, and prefilled rather than removed.
 * The CEO filled both with "see prompt" on their second entry — we were asking for something the
 * prompt already says ("send kobi the time on whatsapp" IS both the intention and the expectation).
 * But the case where the prompt is NOT the expectation is exactly where this page earns its keep:
 * "I expected it to ask before sending" appears in no prompt. An empty field gets "see prompt"; a
 * prefilled one gets edited only when the prompt is wrong, which is precisely when we want their
 * words. Deleting the fields would lose that class entirely.
 *
 * A consequence worth knowing rather than engineering around: an unedited field is now equal to
 * the prompt, so "they agreed" and "they did not look" are indistinguishable in the stored row.
 * That is checkable after the fact by comparing the value to the run's prompt — no extra column
 * needed — and it costs nothing until someone actually wants the distinction.
 *
 * FIELD ORDER FOLLOWS WHO IS THE AUTHORITY. The author is the authority on their intention,
 * expectation and observation — those are taken at face value. We are the authority on CAUSES, so
 * "any idea what went wrong" is labelled a guess, is optional, and is never routed from. It is
 * also deliberately FREE TEXT rather than a menu of our subsystems: a dropdown would teach a
 * tester to guess in our vocabulary, and those guesses then read as authoritative.
 */
export function FeedbackForm({
  runId,
  deviceId,
  prompt,
  onSaved,
}: {
  runId: string;
  deviceId?: string | null;
  /** The prompt they issued. Prefills the two fields it already answers — see the note below. */
  prompt?: string | null;
  onSaved?: (e: FeedbackEntry) => void;
}) {
  const seed = prompt?.trim() ?? "";
  const [f, setF] = useState<NewFeedback>({
    runId,
    deviceId,
    author: remembered("feedback.author"),
    intention: seed,
    expectation: seed,
  });
  // Held until publish: the entry must exist before an image can hang off it.
  const [shots, setShots] = useState<Blob[]>([]);
  const [verdicts, setVerdicts] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const set = (k: keyof NewFeedback) => (e: { target: { value: string } }) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      let saved = await api.addFeedback({ ...f, runId, deviceId, verdicts });
      for (const shot of shots) saved = await api.addAttachment(saved.id, shot);
      // Remembered so they do not retype it every entry. Self-declared, never verified — the
      // dashboard has one shared login, so this records who SAYS they wrote it.
      remember("feedback.author", f.author);
      setDone(true);
      onSaved?.(saved);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const takeImages = (items: DataTransferItemList | null, files: FileList | null) => {
    const found: Blob[] = [];
    for (const it of Array.from(items ?? [])) {
      if (it.kind === "file" && it.type.startsWith("image/")) {
        const f = it.getAsFile();
        if (f) found.push(f);
      }
    }
    for (const f of Array.from(files ?? [])) if (f.type.startsWith("image/")) found.push(f);
    if (found.length) setShots((p) => [...p, ...found]);
    return found.length > 0;
  };

  if (done) return <div className="panel">Published. It will appear below with whatever is done about it.</div>;

  return (
    <div
      className="panel"
      style={{ display: "grid", gap: 8 }}
      // Paste is the path that will actually be used: someone on a laptop looking at a phone
      // screenshot. The upload control below posts the same bytes to the same place.
      onPaste={(e) => {
        if (takeImages(e.clipboardData?.items ?? null, null)) e.preventDefault();
      }}
    >
      <strong>Tell us about this run</strong>
      {/* One tap, and the highest-value thing on the page: a verdict that disagrees with the run's
          own account of itself is the one signal no instrument can produce. Labels describe THEIR
          experience — never our components. */}
      <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
        {VERDICT_LABELS.map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={verdicts.includes(value) ? "chip on" : "chip"}
            aria-pressed={verdicts.includes(value)}
            onClick={() => setVerdicts((p) => (p.includes(value) ? p.filter((v) => v !== value) : [...p, value]))}
          >
            {label}
          </button>
        ))}
      </div>
      {err && <div className="error-bar">{err}</div>}
      <label>
        Your name
        <input value={f.author} onChange={set("author")} placeholder="who is writing this" />
      </label>
      <label>
        What were you trying to do?
        <textarea value={f.intention} onChange={set("intention")} rows={2} />
      </label>
      <label>
        What did you expect to happen?
        <textarea value={f.expectation ?? ""} onChange={set("expectation")} rows={2} />
      </label>
      <label>
        What actually happened?
        <textarea value={f.whatHappened ?? ""} onChange={set("whatHappened")} rows={2} />
      </label>
      <label>
        Any idea what went wrong? <span className="muted">(optional — a guess is useful, not required)</span>
        <textarea value={f.whatWentWrong ?? ""} onChange={set("whatWentWrong")} rows={2} />
      </label>
      <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <label className="muted" style={{ fontSize: 12 }}>
          Screenshot — paste anywhere in this box, or
          <input type="file" accept="image/*" multiple onChange={(e) => takeImages(null, e.target.files)} />
        </label>
        {shots.map((s2, i) => (
          <span key={i} className="chip">
            image {i + 1} ({Math.round(s2.size / 1024)} KB)
            <button onClick={() => setShots((p) => p.filter((_, j) => j !== i))} title="remove">×</button>
          </span>
        ))}
      </div>
      <div className="row" style={{ gap: 8, alignItems: "center" }}>
        <button disabled={busy || !f.author.trim()} onClick={submit}>
          {busy ? "publishing…" : "publish"}
        </button>
        <span className="muted" style={{ fontSize: 12 }}>
          Your words and screenshots are kept for people to read. They are never fed to a model or used for
          automatic scoring.
        </span>
      </div>
    </div>
  );
}
